/**
 * Choosing tags to filter *by*, in the two places that do it: the tournament list's filter bar and the
 * leaderboard and rating scope filters. This is the selection only — which tags are required, which
 * excluded — and the chips' gesture (`TagFilterPicker`).
 *
 * **What a selection matches is decided where it is applied, and the two places differ.** The tournament
 * list sends it to the server, where a required tag is AND and an excluded one vetoes
 * (`TournamentService.Matching`). The scope filters apply §3.2's rule, where a required tag is *at least
 * one* (`candidateRows` in `leaderboards/candidates.ts`). Nothing here matches a tournament, so there is no
 * browser copy of the list's rule to keep in step with the server.
 *
 * Not to be confused with the chips in `BulkTagModal`: those look the same and mean something
 * else entirely — an action to perform, not a set to match.
 */

/** A tag's role in a selection: required, vetoed, or neither. */
export type TagState = 'off' | 'include' | 'exclude';

export interface TagSelection {
  /** Required: all of them on the tournament list, at least one in a scope. */
  include: string[];
  /** Any one of these is enough to reject. */
  exclude: string[];
}

export function tagStateIn(selection: TagSelection, tag: string): TagState {
  if (selection.include.includes(tag)) return 'include';
  if (selection.exclude.includes(tag)) return 'exclude';
  return 'off';
}

/**
 * Moves one tag to its next state, dropping it from whichever list it was in first. Going
 * through here rather than splicing the arrays at the call site is what keeps a tag out of both
 * at once — the combination that matches nothing and that the API rejects outright.
 */
export function cycleTagIn(selection: TagSelection, tag: string): TagSelection {
  const without: TagSelection = {
    include: selection.include.filter((t) => t !== tag),
    exclude: selection.exclude.filter((t) => t !== tag),
  };

  switch (tagStateIn(selection, tag)) {
    case 'off':
      return { ...without, include: [...without.include, tag] };
    case 'include':
      return { ...without, exclude: [...without.exclude, tag] };
    default:
      return without;
  }
}
